import { formatDistanceMeters, formatDurationSeconds, formatRideDate, formatSpeedMps } from '@motogo/shared';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import RideSparkline from './RideSparkline';

import { useRides } from '@/hooks/useRides';

// The real saved-rides list: server rides (GET /me/rides) merged with locally-recorded
// ones, each card a sparkline thumbnail linking to /rides/:id. See CLAUDE.md → Ride Review.
const RideListPage: FC = () => {
  const navigate = useNavigate();
  const { state, refresh } = useRides();

  return (
    <div className="mg-page">
      <header className="mg-page-header">
        {/* Explicit target (not navigate(-1)) so the button always lands on the map,
            even when the list was reached via a deep link with no history behind it. */}
        <button className="mg-icon-btn" onClick={() => navigate('/')} aria-label="Back to map" title="Back to map">
          <ArrowLeft size={26} />
        </button>
        <h1 className="mg-page-title">Rides</h1>
      </header>

      {state.status === 'loading' && (
        <div className="mg-page-body">
          <p className="mg-empty">Loading rides…</p>
        </div>
      )}

      {state.status === 'error' && (
        <div className="mg-page-body">
          <p className="mg-empty">Couldn&rsquo;t load rides</p>
          <p className="mg-empty-sub">{state.error}</p>
          <button
            className="mg-btn mg-btn-outline"
            style={{ width: 'auto', padding: '10px 24px', marginTop: 12 }}
            onClick={refresh}
          >
            Retry
          </button>
        </div>
      )}

      {state.status === 'ready' && state.rides.length === 0 && (
        <div className="mg-page-body">
          <p className="mg-empty">No saved rides yet</p>
          <p className="mg-empty-sub">Your recorded rides will appear here.</p>
        </div>
      )}

      {state.status === 'ready' && state.rides.length > 0 && (
        <ul className="mg-ride-list">
          {state.rides.map((ride) => (
            <li key={ride.id}>
              <button className="mg-ride-card" onClick={() => navigate(`/rides/${ride.id}`)}>
                <RideSparkline polyline={ride.overviewPolyline} />
                <div className="mg-ride-meta">
                  <span className="mg-ride-name">{ride.name ?? formatRideDate(ride.startedAt)}</span>
                  {ride.name ? <span className="mg-ride-date">{formatRideDate(ride.startedAt)}</span> : null}
                  <div className="mg-ride-stats">
                    <span>{formatDistanceMeters(ride.distanceMeters)}</span>
                    <span>{formatDurationSeconds(ride.durationSeconds)}</span>
                    <span>{formatSpeedMps(ride.avgSpeedMps)} avg</span>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RideListPage;
